/**
 * User-cancellation helpers (the Stop button).
 *
 * Deliberately side-effect-free: it imports only the `MessageWarning` *type*,
 * so retrieval services (graphService, azureAISearchService) can pull these in
 * without transitively loading openAIService's module-level Azure config — a
 * trap that crashes tests which only partially mock `azureConfig`.
 */

import type { MessageWarning } from '@/types';

/**
 * Build the typed error used for user-initiated cancellation. Shaped so the
 * chat catch path renders the friendly "You stopped the response…" system
 * message: `name === 'AbortError'` (matches fetch's own AbortError) and a
 * `warning` of type `cancelled` carried up front, so callers that bubble it
 * through graceful-degradation catch blocks don't have to re-classify.
 *
 * Kept byte-for-byte in sync with `classifyError`'s `cancelled` branch in
 * openAIService so a cancel looks identical however it propagates.
 */
export function createCancellationError(): Error & { warning: MessageWarning } {
  const err = new Error('cancelled');
  err.name = 'AbortError';
  const warning: MessageWarning = {
    type: 'cancelled',
    title: 'Generation Cancelled',
    message: 'You stopped the response before it finished.',
    details: 'cancelled',
  };
  return Object.assign(err, { warning });
}

/**
 * Throw a typed cancellation if `signal` is already aborted. Use at the top of
 * a retry loop or before a fetch so an abort that fired between phases
 * short-circuits immediately instead of doing wasted work.
 */
export function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) {
    throw createCancellationError();
  }
}

/**
 * Decide whether a thrown error represents the user pressing Stop, as opposed
 * to a provider/network failure that callers should degrade past.
 *
 * Every retrieval phase (AI Search, M365, web search) wraps its fetch in a
 * graceful-degradation catch ("continue without this source"). A user cancel
 * surfaces inside those same catch blocks as an AbortError, so each catch uses
 * this guard to RE-THROW cancellations (aborting the whole turn) while still
 * swallowing genuine provider failures.
 *
 * @param error  The caught value (typed `unknown` — read its fields defensively).
 * @param signal The AbortSignal handed to the in-flight call, if any.
 * @returns true when the turn should be cancelled, false to degrade.
 */
export function isUserCancellation(
  error: unknown,
  signal?: AbortSignal
): boolean {
  // An aborted signal is authoritative proof of intent regardless of how the
  // error was reshaped on the way up the stack.
  if (signal?.aborted) {
    return true;
  }
  // Otherwise sniff the error: fetch rejects an aborted request with a
  // DOMException named 'AbortError', and createCancellationError() above sets
  // name 'AbortError' / message 'cancelled'.
  const name = (error as { name?: unknown } | null | undefined)?.name;
  const message = (error as { message?: unknown } | null | undefined)?.message;
  return name === 'AbortError' || message === 'cancelled';
}
